"use client";

import dynamic from "next/dynamic";
import Link from "next/link";

import { motion } from "motion/react";

import { Button } from "components/Button";
import { NavigationMenu } from "components/Header/components/NavigationMenu";

import { cn } from "lib/cn";

import Logo from "./components/Logo";
import type { Logo as LogoType } from "./components/Logo";

import MoreLogoImage from "/images/common/more-logo.svg";
import Shelf from "/images/common/shelf.svg";

import ShoppingBagIcon from "@material-symbols/svg-700/sharp/shopping_bag.svg";

interface HeaderProps {
  className?: string;
  logo?: LogoType;
  logoHref?: string;
  overlay?: boolean;
  hideNavigationMenu?: boolean;
  navigationMenuItems?: Array<
    "find-a-store" | "about" | "cookbook" | "investors"
  >;
}

const Header = ({
  className,
  logo = MoreLogoImage,
  logoHref = "/",
  overlay,
  hideNavigationMenu = false,
  navigationMenuItems = ["find-a-store", "about", "cookbook", "investors"],
}: HeaderProps) => {
  return (
    <>
      {/* md:top-17 is used to compensate for the sticky navgiation menu's negative margin. The menu's height is 68px (17rem) and when on breakpoints below md, it gets hidden. */}
      <motion.header
        className={cn(
          "pointer-events-none relative top-0 z-50 flex h-17 min-h-17 w-full max-w-480 items-center justify-center p-3 md:h-31",
          overlay
            ? !hideNavigationMenu
              ? "absolute md:top-17"
              : "absolute"
            : "relative",
          className,
        )}
      >
        <motion.div
          id="shelf"
          className={`absolute inline-block origin-top-left ${overlay ? "top-3 left-3 md:top-5 md:left-5" : "top-0 left-0"}`}
          initial={{
            scale: 0,
          }}
          animate={{
            scale: 1,
            transition: {
              duration: 0.5,
            },
          }}
          exit={{ scale: 0 }}
        >
          <Link className="pointer-events-auto" href={logoHref}>
            <Shelf
              className={`h-20 md:h-30 ${overlay ? "fill-white" : "fill-orange-60"}`}
            />
            <Logo
              logo={logo}
              className={`absolute top-1/2 left-1/2 -mt-[5%] -ml-[10%] h-6 w-auto -translate-x-1/2 -translate-y-1/2 md:h-8 ${logo == MoreLogoImage ? (overlay ? "fill-orange-60" : "fill-white") : ""}`}
            />
            <span className="sr-only absolute size-0 opacity-0">Logo</span>
          </Link>
        </motion.div>
        <Button
          variant="secondary"
          size="large"
          className={`absolute z-50 max-md:h-12 ${overlay ? "top-6 right-6 md:top-10 md:right-10" : "top-4 right-3 md:top-auto md:right-5"}`}
          asChild
        >
          <Link className="pointer-events-auto" href="/orders" target="_blank">
            <ShoppingBagIcon className="size-4 fill-white" />
            Orders
          </Link>
        </Button>
      </motion.header>
      {!hideNavigationMenu && (
        <NavigationMenu items={navigationMenuItems} overlay={overlay} />
      )}
    </>
  );
};

export default dynamic(() => Promise.resolve(Header), { ssr: false });
